import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type CannedResponse } from "../../api";
import { useI18n } from "../../lib/i18n";
import { useDialogs } from "../../components/Dialog";
import { PlanLimitNotice, useFeature, usePlanLimit } from "../../components/PlanNotice";
import { Modal, ReadOnlyNote, Section, StatusLine, useSites, useStatus } from "./common";

/**
 * Saved replies, reached from the reply box by typing "/" and the shortcut.
 * Shortcuts are lower-case letters, digits and dashes: they are typed quickly
 * mid-sentence, and a space or a capital would make one unreachable.
 */

type Draft = {
  shortcut: string;
  title: string;
  body: string;
  siteId: number | null;
};
const EMPTY: Draft = { shortcut: "", title: "", body: "", siteId: null };

export function normaliseShortcut(raw: string): string {
  return raw
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 40);
}

function Editor({
  initial,
  sites,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: Draft;
  sites: Array<{ id: number; name: string }>;
  submitLabel: string;
  onSubmit: (draft: Draft) => Promise<void>;
  onCancel?: () => void;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.canned;
  const [draft, setDraft] = useState(initial);
  const valid = draft.shortcut.length > 0 && draft.title.trim().length > 0 && draft.body.trim().length > 0;
  return (
    <form
      class="grid-2"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({
          ...draft,
          title: draft.title.trim(),
          body: draft.body.trim(),
        });
      }}
    >
      <label>
        {t.shortcut} <span class="hint">{t.shortcutHint(draft.shortcut)}</span>
        <input
          type="text"
          value={draft.shortcut}
          maxLength={40}
          onInput={(e) =>
            setDraft({
              ...draft,
              shortcut: normaliseShortcut(e.currentTarget.value),
            })
          }
          required
        />
      </label>
      <label>
        {t.title}
        <input
          type="text"
          value={draft.title}
          maxLength={120}
          onInput={(e) => setDraft({ ...draft, title: e.currentTarget.value })}
          required
        />
      </label>
      <label class="span-2">
        {t.body}
        <textarea
          class="plain"
          value={draft.body}
          maxLength={4000}
          onInput={(e) => setDraft({ ...draft, body: e.currentTarget.value })}
          required
        />
      </label>
      {sites.length > 1 ? (
        <label>
          {t.scope}
          <select
            value={draft.siteId ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                siteId: e.currentTarget.value ? Number(e.currentTarget.value) : null,
              })
            }
          >
            <option value="">{t.allSites}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {t.onlySite(s.name)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div class="span-2 dialog-actions">
        {onCancel ? (
          <button type="button" class="btn" onClick={onCancel}>
            {all.common.cancel}
          </button>
        ) : null}
        <button class="btn primary" disabled={!valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function CannedTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t: all } = useI18n();
  const t = all.canned;
  const { sites } = useSites();
  const [items, setItems] = useState<CannedResponse[]>([]);
  /** The reply being written or changed, in a window; "new" for a fresh one. */
  const [editor, setEditor] = useState<CannedResponse | "new" | null>(null);
  const [status, show, fail] = useStatus();
  const dialogs = useDialogs();
  const { limit, catchLimit } = usePlanLimit();
  // Free has no saved replies at all: the list still shows what was written
  // before a downgrade (nothing is deleted), but nothing new can be written.
  const canEdit = useFeature("cannedReplies");

  const report = useCallback(
    (err: unknown): void => {
      if (!catchLimit(err)) fail(err);
    },
    [catchLimit, fail],
  );

  const reload = useCallback(async () => {
    try {
      const res = await api.canned();
      setItems(res.canned.sort((a, b) => a.shortcut.localeCompare(b.shortcut)));
    } catch (err) {
      report(err);
    }
  }, [report]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      <ReadOnlyNote isAdmin={isAdmin} />
      {/* Without the feature there is nothing to try and fail at, so the
          notice stands in for the editor rather than waiting for a 402. */}
      <PlanLimitNotice limit={canEdit ? limit : "cannedReplies"} />
      <StatusLine status={status} />
      <Section
        title={t.listTitle}
        intro={t.listIntro}
        actions={
          isAdmin && canEdit ? (
            <button class="btn primary" onClick={() => setEditor("new")}>
              + {t.newTitle}
            </button>
          ) : null
        }
      >
        {items.length === 0 ? <p class="hint">{t.none}</p> : null}
        <ul class="item-list">
          {items.map((item) => (
            <li key={item.id}>
              <div class="item-main">
                <span>
                  <code>/{item.shortcut}</code> <strong>{item.title}</strong>
                  {item.siteId ? (
                    <span class="chip">{sites.find((s) => s.id === item.siteId)?.name ?? all.common.site}</span>
                  ) : null}
                </span>
                <span class="item-body">{item.body}</span>
                <span class="hint">{t.used(item.usageCount)}</span>
              </div>
              {isAdmin && canEdit ? (
                <div class="item-actions">
                  <button class="btn sm" onClick={() => setEditor(item)}>
                    {all.common.edit}
                  </button>
                  <button
                    class="btn sm danger"
                    onClick={() => {
                      void dialogs
                        .confirm({
                          title: t.confirmDelete(item.shortcut),
                          confirmLabel: all.common.delete,
                          danger: true,
                        })
                        .then((sure) => {
                          if (!sure) return;
                          return api.deleteCanned(item.id).then(() => {
                            show("ok", t.deleted(item.shortcut));
                            return reload();
                          });
                        })
                        .catch(report);
                    }}
                  >
                    {all.common.delete}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Section>

      {editor ? (
        <Modal title={editor === "new" ? t.newTitle : `/${editor.shortcut}`} wide onClose={() => setEditor(null)}>
          <Editor
            initial={
              editor === "new"
                ? EMPTY
                : {
                    shortcut: editor.shortcut,
                    title: editor.title,
                    body: editor.body,
                    siteId: editor.siteId,
                  }
            }
            sites={sites}
            submitLabel={editor === "new" ? all.common.add : all.common.save}
            onCancel={() => setEditor(null)}
            onSubmit={async (draft) => {
              try {
                if (editor === "new") await api.createCanned(draft);
                else await api.updateCanned(editor.id, draft);
                show("ok", editor === "new" ? t.added(draft.shortcut) : t.saved(draft.shortcut));
                setEditor(null);
                await reload();
              } catch (err) {
                report(err);
              }
            }}
          />
        </Modal>
      ) : null}
      {dialogs.dialog}
    </>
  );
}
